import React, { useState, useEffect, useRef } from "react";
import ImageGallery from "react-image-gallery";
import "react-image-gallery/styles/css/image-gallery.css";
import { FaArrowLeft, FaEye, FaEyeSlash, FaList, FaPlay, FaExpand, FaSync, FaDownload, FaVolumeUp, FaVolumeMute, FaStop } from "react-icons/fa";
import Button from "react-bootstrap/Button";
import Card from "react-bootstrap/Card";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Badge from "react-bootstrap/Badge";
import Form from "react-bootstrap/Form";
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

// Utility function to detect if an image is blank/white
const detectBlankSlide = (imageUrl) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);
      
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      
      let transparentPixels = 0;
      let totalPixels = data.length / 4;
      
      // Sample pixels to improve performance (check every 10th pixel)
      for (let i = 0; i < data.length; i += 40) {
        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];
        const a = data[i + 3];
        
        // Check if pixel is transparent (alpha = 0)
        if (a === 0) {
          transparentPixels++;
        }
      }
      
      const transparentPercentage = (transparentPixels / (totalPixels / 10)) * 100;
      resolve(transparentPercentage > 95); // Consider blank if >95% transparent
    };
    img.onerror = () => {
      resolve(false); // If image fails to load, assume not blank
    };
    img.src = imageUrl;
  });
};

// Utility function to extract slide number from URL
const extractSlideNumber = (imageUrl) => {
  // Extract the slide_id from the URL
  // URL format: /download-slide?password=...&workshop_id=...&slide_id=filename.png
  const urlParams = new URLSearchParams(imageUrl.split('?')[1]);
  const slideId = urlParams.get('slide_id');
  
  if (slideId) {
    // Remove .png extension and return the slide number
    return slideId.replace('.png', '');
  }
  
  // Fallback: try to extract from the URL path
  const match = imageUrl.match(/slide_id=([^.]+)/);
  return match ? match[1] : 'Unknown';
};

const Slideshow = (props) => {
  const [images, setImages] = useState([]);
  const [allWorkshopIds, setAllWorkshopIds] = useState([]);
  const [submittedWorkshopId, setSubmittedWorkshopId] = useState(null);
  const [loadingSlides, setLoadingSlides] = useState(false);
  const [refreshingSlides, setRefreshingSlides] = useState(false);
  const [showSlideshow, setShowSlideshow] = useState(false);
  const [blankSlides, setBlankSlides] = useState([]);
  const [analyzingSlides, setAnalyzingSlides] = useState(false);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [cacheBuster, setCacheBuster] = useState(0);
  const [downloadingPDF, setDownloadingPDF] = useState(false);
  const [blankDetectionEnabled, setBlankDetectionEnabled] = useState(true);
  const [submissions, setSubmissions] = useState([]);
  const [currentAudio, setCurrentAudio] = useState(null);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [audioVolume, setAudioVolume] = useState(0.7);
  const [audioMuted, setAudioMuted] = useState(false);
  const [audioDurations, setAudioDurations] = useState({});
  const [showInactiveWorkshops, setShowInactiveWorkshops] = useState(false);
  const audioRef = useRef(null);

  // Fetch list of workshops on mount.
  useEffect(() => {
    document.title = `Slideshow | ${props.config.subtitle} | ${props.config.brand}`;
    fetchWorkshops();
  }, [showInactiveWorkshops]);

  // Function to fetch workshops
  const fetchWorkshops = () => {
    fetch("/list-workshops", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        password: localStorage.getItem("frontend-password"),
        active_only: !showInactiveWorkshops,
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        // Store the list of workshop IDs.
        setAllWorkshopIds(data.workshops);
      })
      .catch((error) => {
        console.error("Error fetching workshops:", error);
      });
  };

  // Function to get audio duration
  const getAudioDuration = (audioUrl) => {
    return new Promise((resolve) => {
      const audio = new Audio();
      audio.addEventListener('loadedmetadata', () => {
        resolve(audio.duration);
      });
      audio.addEventListener('error', () => {
        resolve(0);
      });
      audio.src = audioUrl;
    });
  };

  // Function to format duration in MM:SS format
  const formatDuration = (seconds) => {
    if (!seconds || seconds === 0) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Function to fetch slides for a workshop
  const fetchSlides = async (workshopId, cacheBusterDate) => {
    const requestData = {
      password: localStorage.getItem("frontend-password"),
      workshop_id: workshopId,
    };

    try {
      const response = await fetch("/get-slides", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestData),
      });
      
      const data = await response.json();
      const submissions = data.submissions || [];
      setSubmissions(submissions);
      
      // Extract slides from submissions
      const slides = submissions
        .filter(submission => submission.slide !== null)
        .map(submission => submission.slide);
      
      // Add cache-busting parameter to image URLs (always use it)
      let imageItems = slides.map((slide) => {
        const imageUrl = `${slide}${slide.includes('?') ? '&' : '?'}t=${cacheBusterDate}`;
        return { original: imageUrl };
      });
      
      // Add cache-busting parameter to audio URLs as well
      const submissionsWithCacheBuster = submissions.map(submission => ({
        ...submission,
        audio: submission.audio ? `${submission.audio}${submission.audio.includes('?') ? '&' : '?'}t=${cacheBusterDate}` : null
      }));
      
      // Sort slides alphabetically by slide number
      imageItems.sort((a, b) => {
        const slideNumberA = extractSlideNumber(a.original);
        const slideNumberB = extractSlideNumber(b.original);
        return slideNumberA.localeCompare(slideNumberB);
      });
      
      // Sort submissions to match the sorted imageItems
      const sortedSubmissions = submissionsWithCacheBuster.sort((a, b) => {
        if (!a.slide || !b.slide) return 0;
        const slideNumberA = extractSlideNumber(a.slide);
        const slideNumberB = extractSlideNumber(b.slide);
        return slideNumberA.localeCompare(slideNumberB);
      });
      
      setImages(imageItems);
      setSubmissions(sortedSubmissions);

      // Set loading to false immediately after basic slide data is loaded
      setLoadingSlides(false);

      // Load audio durations in the background
      const durations = {};
      for (const submission of sortedSubmissions) {
        if (submission.audio) {
          try {
            const duration = await getAudioDuration(submission.audio);
            durations[submission.audio] = duration;
          } catch (error) {
            console.error('Error loading audio duration:', error);
            durations[submission.audio] = 0;
          }
        }
      }
      setAudioDurations(durations);

      let imageSlides = [];
      for (let i = 0; i < imageItems.length; i++) {
        imageSlides.push(imageItems[i].original);
      }

      // Only analyze slides for blank detection if enabled
      if (blankDetectionEnabled) {
        // Run blank detection in the background without blocking the UI
        analyzeSlidesForBlank(imageSlides);
      } else {
        setBlankSlides([]); // Clear blank slides when detection is disabled
      }
      
      return slides;
    } catch (error) {
      console.error("Error fetching slides:", error);
      throw error;
    }
  };

  // Fetch slides whenever a workshop is submitted.
  useEffect(() => {
    if (!submittedWorkshopId) return;

    setLoadingSlides(true);
    const cacheBusterDate = Date.now();
    setCacheBuster(cacheBusterDate);
    fetchSlides(submittedWorkshopId, cacheBusterDate)
      .catch(() => {
        setLoadingSlides(false);
      });
  }, [submittedWorkshopId]);

  // Refresh slides function
  const handleRefreshSlides = async () => {
    if (!submittedWorkshopId) return;
    
    setRefreshingSlides(true);
    setBlankSlides([]); // Clear blank slides immediately when refreshing
    setAudioDurations({}); // Clear audio durations to force reload
    // Update cache buster to force re-render of all images
    const cacheBusterDate = Date.now();
    setCacheBuster(cacheBusterDate);
    try {
      // Force refresh with cache-busting
      await fetchSlides(submittedWorkshopId, cacheBusterDate);
    } catch (error) {
      // Error already logged in fetchSlides
    } finally {
      setRefreshingSlides(false);
    }
  };

  // Analyze slides to detect blank/white slides
  const analyzeSlidesForBlank = async (slides) => {
    
    setAnalyzingSlides(true);
    const blankIndices = [];
    
    for (let i = 0; i < slides.length; i++) {
      const isBlank = await detectBlankSlide(slides[i]);
      if (isBlank) {
        blankIndices.push(i + 1); // +1 for 1-based indexing
      }
    }
    setBlankSlides(blankIndices);
    setAnalyzingSlides(false);
  };

  // Function to handle audio playback
  const playAudio = (audioUrl) => {
    if (audioRef.current) {
      // Stop current audio if playing
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    
    if (audioUrl) {
      setCurrentAudio(audioUrl);
      audioRef.current.src = audioUrl;
      audioRef.current.volume = audioMuted ? 0 : audioVolume;
      audioRef.current.play().then(() => {
        setIsAudioPlaying(true);
      }).catch(error => {
        console.error('Error playing audio:', error);
        setIsAudioPlaying(false);
      });
    } else {
      setCurrentAudio(null);
      setIsAudioPlaying(false);
    }
  };

  // Function to stop audio
  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setIsAudioPlaying(false);
  };

  // Handle audio ended event
  const handleAudioEnded = () => {
    setIsAudioPlaying(false);
  };

  // Function to play audio for current slide during slideshow
  const playAudioForCurrentSlide = () => {
    if (!showSlideshow) return;
    
    const filteredSlides = getFilteredSlides();
    if (currentSlideIndex >= 0 && currentSlideIndex < filteredSlides.length) {
      const currentSlide = filteredSlides[currentSlideIndex];
      const slideNumber = extractSlideNumber(currentSlide.original);
      
      // Find submission with matching slide
      const submission = submissions.find(sub => {
        if (!sub.slide) return false;
        const subSlideNumber = extractSlideNumber(sub.slide);
        return subSlideNumber === slideNumber;
      });
      
      if (submission && submission.audio) {
        playAudio(submission.audio);
      } else {
        stopAudio();
        setCurrentAudio(null);
      }
    }
  };

  // Effect to play audio when slide changes during slideshow
  useEffect(() => {
    if (showSlideshow) {
      playAudioForCurrentSlide();
    }
  }, [currentSlideIndex, showSlideshow]);

  // Effect to stop audio when leaving slideshow
  useEffect(() => {
    if (!showSlideshow) {
      stopAudio();
    }
  }, [showSlideshow]);

  // Function to handle clicking on a slide thumbnail
  const handleSlideClick = (slideIndex) => {
    // Find the corresponding index in the filtered slideshow
    const nonBlankSlides = images.filter((_, index) => !blankSlides.includes(index + 1));
    const filteredIndex = nonBlankSlides.findIndex((_, index) => {
      // Find the original index that corresponds to this filtered index
      let originalIndex = 0;
      let filteredCount = 0;
      while (filteredCount <= index) {
        if (!blankSlides.includes(originalIndex + 1)) {
          filteredCount++;
        }
        originalIndex++;
      }
      return originalIndex - 1 === slideIndex;
    });
    
    setCurrentSlideIndex(Math.max(0, filteredIndex));
    setShowSlideshow(true);
  };

  // Get filtered slides for slideshow (excluding blank slides)
  const getFilteredSlides = () => {
    if (!images || images.length === 0) {
      return []; // Return empty array if no images
    }
    if (!blankDetectionEnabled) {
      return images; // Return all slides if blank detection is disabled
    }
    return images.filter((_, index) => !blankSlides.includes(index + 1));
  };

  // Function to enter fullscreen slideshow
  const enterFullscreenSlideshow = () => {
    setCurrentSlideIndex(0);
    setShowSlideshow(true);
    
    // Request fullscreen after a short delay to ensure slideshow is rendered
    setTimeout(() => {
      const slideshowContainer = document.querySelector('.image-gallery');
      if (slideshowContainer && slideshowContainer.requestFullscreen) {
        slideshowContainer.requestFullscreen().catch(err => {
          console.log('Fullscreen request failed:', err);
        });
      }
    }, 100);
  };

  // Handle ESC key to go back to overview
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && showSlideshow) {
        // Exit fullscreen first if in fullscreen
        if (document.fullscreenElement) {
          document.exitFullscreen().catch(err => {
            console.log('Exit fullscreen failed:', err);
          });
        }
        setShowSlideshow(false);
      }
    };

    if (showSlideshow) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showSlideshow]);

  // Function to download slideshow as PDF
  const downloadSlideshowAsPDF = async () => {
    setDownloadingPDF(true);
    
    try {
      // Use square format (200mm x 200mm) instead of A4 landscape
      const pdf = new jsPDF('p', 'mm', [200, 200]); // Square format
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      
      // Filter out blank slides if user wants to exclude them and detection is enabled
      const slidesToInclude = blankDetectionEnabled 
        ? images.filter((_, index) => !blankSlides.includes(index + 1))
        : images;
      
      for (let i = 0; i < slidesToInclude.length; i++) {
        const slide = slidesToInclude[i];
        
        try {
          // Create a temporary image element
          const img = new Image();
          img.crossOrigin = "anonymous";
          
          await new Promise((resolve, reject) => {
            img.onload = resolve;
            img.onerror = reject;
            img.src = slide.original;
          });
          
          // Create a canvas to draw the image
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          // Set canvas dimensions to match the image
          canvas.width = img.width;
          canvas.height = img.height;
          
          // Draw the image on canvas
          ctx.drawImage(img, 0, 0);
          
          // Convert canvas to image data
          const imgData = canvas.toDataURL('image/jpeg', 0.95);
          
          // Remove all borders - fill the entire page
          const x = 0;
          const y = 0;
          const width = pageWidth;
          const height = pageHeight;
          
          // Add new page for each slide (except the first one)
          if (i > 0) {
            pdf.addPage();
          }
          
          // Add the image to PDF, filling the entire page with no borders
          pdf.addImage(imgData, 'JPEG', x, y, width, height);
          
        } catch (error) {
          console.error(`Error processing slide ${i + 1}:`, error);
          // Continue with next slide even if one fails
        }
      }
      
      // Save the PDF
      const filename = `${submittedWorkshopId}_slideshow.pdf`;
      pdf.save(filename);
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    } finally {
      setDownloadingPDF(false);
    }
  };

  // Function to download MP3 file
  const downloadMP3 = async (audioUrl, studentId) => {
    try {
      // Extract the audio_id from the URL
      const urlParams = new URLSearchParams(audioUrl.split('?')[1]);
      const audioId = urlParams.get('slide_id'); // Using slide_id parameter for audio files too
      
      // Construct the download URL for audio with cache busting
      const downloadUrl = `/download-audio?password=${encodeURIComponent(localStorage.getItem("frontend-password"))}&workshop_id=${encodeURIComponent(submittedWorkshopId)}&audio_id=${encodeURIComponent(audioId)}&t=${Date.now()}`;
      
      // Create a temporary link and trigger download
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `${studentId}.mp3`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading MP3:', error);
      alert('Error downloading audio file. Please try again.');
    }
  };

  // While the list of workshops is loading, show a full-page spinner.
  const orangeHeader = (
    <div style={{
      backgroundColor: '#f05f40',
      padding: '0.5rem 1rem',
      display: 'flex',
      alignItems: 'center',
    }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
        <span style={{ color: 'white', fontSize: '10px', fontWeight: 'bold', lineHeight: '1.2', fontFamily: 'Roboto, sans-serif' }}>
          {props.config.brand}
        </span>
        <span style={{ color: 'white', fontSize: '8px', fontWeight: 'bold', lineHeight: '1.2', fontFamily: 'Roboto, sans-serif' }}>
          {props.config.subtitle}
        </span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', marginLeft: '8px', marginRight: '8px' }}>
        <div style={{ width: '1px', height: '28px', backgroundColor: 'white', opacity: '0.7' }}></div>
      </div>
      <span style={{ color: 'white', fontSize: '24px', fontWeight: 'bold', lineHeight: '1.2', fontFamily: 'Roboto, sans-serif' }}>
        {props.config.title}
      </span>
    </div>
  );

  if (allWorkshopIds.length === 0) {
    return (
      <>
        {orangeHeader}
        <div className="d-flex justify-content-center align-items-center vh-100">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading workshops...</span>
          </div>
        </div>
      </>
    );
  }

  let configSection = <></>;

  if (submittedWorkshopId === null) {
    configSection = (
      <div className="container my-4">
        {/* Config Section */}
        <div className="card shadow-sm mb-4">
          <div className="card-body">
            <h4 className="card-title mb-4 text-center">Select a Workshop</h4>
            
            {/* Settings Toggles */}
            <div className="mb-4">
              <div className="d-flex justify-content-center align-items-center gap-4">
                <Form.Check
                  type="switch"
                  id="blank-detection-toggle"
                  label="Enable blank slide detection"
                  checked={blankDetectionEnabled}
                  onChange={(e) => setBlankDetectionEnabled(e.target.checked)}
                />
                <Form.Check
                  type="switch"
                  id="inactive-workshops-toggle"
                  label="View inactive workshops"
                  checked={showInactiveWorkshops}
                  onChange={(e) => setShowInactiveWorkshops(e.target.checked)}
                />
              </div>
            </div>
            
            {allWorkshopIds.length === 0 ? (
              <div className="text-center py-4">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Loading workshops...</span>
                </div>
                <p className="mt-3 text-muted">Loading available workshops...</p>
              </div>
            ) : (
              <Row className="g-3">
                {allWorkshopIds.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase())).map((id) => (
                  <Col key={id} xs={12} sm={6} md={4} lg={3}>
                    <Card 
                      className="h-100 workshop-card"
                      style={{ 
                        cursor: 'pointer',
                        transition: 'all 0.2s ease-in-out',
                        border: '1px solid #dee2e6'
                      }}
                      onClick={() => {
                        setSubmittedWorkshopId(id);
                        setBlankSlides([]); // Clear blank slides state when selecting a new workshop
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
                      }}
                    >
                      <Card.Body className="d-flex flex-column justify-content-center align-items-center text-center p-4">
                        <div className="mb-3">
                          <FaList style={{ fontSize: '2rem', color: '#6c757d' }} />
                        </div>
                        <h6 className="card-title mb-0 fw-bold">{id}</h6>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {orangeHeader}
      {/* Hidden audio element for playback */}
      <audio
        ref={audioRef}
        onEnded={handleAudioEnded}
        style={{ display: 'none' }}
      />
      
      {configSection}
      <div className="container my-4">
        {/* Slide Show Section */}
        {submittedWorkshopId &&
          (loadingSlides ? (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ height: "400px" }}
            >
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading slides...</span>
              </div>
            </div>
          ) : showSlideshow ? (
            // Fullscreen Slideshow View
            <div>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <Button
                  variant="secondary"
                  onClick={() => setShowSlideshow(false)}
                >
                  <FaArrowLeft /> Back to Overview
                </Button>
                
                <div className="text-center">
                  <h5 className="mb-0">Workshop: {submittedWorkshopId}</h5>
                  <small className="text-muted">
                    {getFilteredSlides().length} of {images.length} slides • {blankDetectionEnabled && blankSlides.length > 0 && `${blankSlides.length} blank slides skipped`}
                  </small>
                </div>

                <div className="d-flex gap-2">
                  <Button
                    variant="outline-primary"
                    onClick={handleRefreshSlides}
                    disabled={refreshingSlides}
                  >
                    <FaSync className={`me-2 ${refreshingSlides ? 'fa-spin' : ''}`} />
                    {refreshingSlides ? 'Refreshing...' : 'Refresh'}
                  </Button>
                  
                  <Button
                    variant="primary"
                    onClick={enterFullscreenSlideshow}
                  >
                    <FaExpand className="me-2" />
                    View Slideshow
                  </Button>
                </div>
              </div>

              {/* Audio Controls for Slideshow */}
              <Card className="mb-3">
                <Card.Body className="py-2">
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center">
                      {currentAudio ? (
                        <>
                          <span className="small">
                            {getFilteredSlides().length > 0 && currentSlideIndex < getFilteredSlides().length ? 
                              extractSlideNumber(getFilteredSlides()[currentSlideIndex].original) : 'No slide'}
                            <span style={{ marginLeft: '20px', marginRight: '-3px' }}>
                              Audio:
                            </span>
                            <span className="text-muted ms-2">
                              ({formatDuration(audioDurations[currentAudio])})
                            </span>
                          </span>
                        </>
                      ) : (
                        <span className="small">
                          {getFilteredSlides().length > 0 && currentSlideIndex < getFilteredSlides().length ? 
                            extractSlideNumber(getFilteredSlides()[currentSlideIndex].original) : 'No slide'}
                        </span>
                      )}
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => {
                          if (isAudioPlaying) {
                            stopAudio();
                          } else {
                            playAudio(currentAudio);
                          }
                        }}
                        disabled={!currentAudio}
                      >
                        {isAudioPlaying ? (
                          <>
                            <FaStop className="me-1" />
                            Stop
                          </>
                        ) : (
                          <>
                            <FaPlay className="me-1" />
                            Play
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </Card.Body>
              </Card>

              <ImageGallery
                items={getFilteredSlides()}
                showPlayButton={false}
                showNav={true}
                showThumbnails={false}
                infinite={false}
                slideDuration={100}
                startIndex={currentSlideIndex}
                onSlide={(index) => setCurrentSlideIndex(index)}
              />
              
              {getFilteredSlides().length === 0 && (
                <div className="text-center py-4">
                  <p className="text-muted">No slides available for slideshow.</p>
                </div>
              )}
            </div>
          ) : (
            // Overview View (Default)
            <div>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <Button
                  variant="secondary"
                  onClick={() => setSubmittedWorkshopId(null)}
                >
                  <FaArrowLeft /> Back to Workshop Selection
                </Button>
                
                <div className="text-center">
                  <h4 className="mb-0">Workshop: {submittedWorkshopId}</h4>
                  <small className="text-muted">
                    {images.length} slides • {blankDetectionEnabled && blankSlides.length > 0 && `${blankSlides.length} blank slides`}
                  </small>
                </div>
                
                <div className="d-flex gap-2">
                  <Button
                    variant="outline-primary"
                    onClick={handleRefreshSlides}
                    disabled={refreshingSlides}
                  >
                    <FaSync className={`me-2 ${refreshingSlides ? 'fa-spin' : ''}`} />
                    {refreshingSlides ? 'Refreshing...' : 'Refresh'}
                  </Button>
                  
                  <Button
                    variant="outline-success"
                    onClick={downloadSlideshowAsPDF}
                    disabled={downloadingPDF || images.length === 0}
                  >
                    <FaDownload className={`me-2 ${downloadingPDF ? 'fa-spin' : ''}`} />
                    {downloadingPDF ? 'Generating PDF...' : 'Download PDF'}
                  </Button>
                  
                  <Button
                    variant="primary"
                    size="lg"
                    onClick={enterFullscreenSlideshow}
                  >
                    <FaExpand className="me-2" />
                    View Slideshow
                  </Button>
                </div>
              </div>

              {/* Blank Detection Toggle for Slideshow View */}
              <Card className="mb-4">
                <Card.Header>
                  <div className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">Settings</h5>
                    <Form.Check
                      type="switch"
                      id="blank-detection-toggle-slideshow"
                      label="Enable blank slide detection"
                      checked={blankDetectionEnabled}
                      onChange={(e) => {
                        setBlankDetectionEnabled(e.target.checked);
                        if (e.target.checked && images.length > 0) {
                          // Re-analyze slides if detection is re-enabled
                          const imageSlides = images.map(img => img.original);
                          analyzeSlidesForBlank(imageSlides);
                        } else {
                          setBlankSlides([]);
                        }
                      }}
                    />
                  </div>
                </Card.Header>
              </Card>

              <Card className="mb-4">
                <Card.Header>
                  <h5 className="mb-0">
                    Overview
                  </h5>
                </Card.Header>
                <Card.Body>
                  {refreshingSlides ? (
                    <div className="text-center py-3">
                      <div className="spinner-border spinner-border-sm me-2" role="status">
                        <span className="visually-hidden">
                          Refreshing slides...
                        </span>
                      </div>
                      Refreshing slides...
                    </div>
                  ) : (
                    <>
                      <div className="mb-3">
                        <strong>Submissions:</strong> {submissions.length} students
                        {blankDetectionEnabled && blankSlides.length > 0 && (
                          <span className="ms-3">
                            <Badge bg="warning" text="dark">
                              {blankSlides.length} blank slide{blankSlides.length !== 1 ? 's' : ''}
                            </Badge>
                          </span>
                        )}
                      </div>
                      
                      {blankDetectionEnabled && blankSlides.length > 0 && (
                        <div className="mb-3">
                          <strong>Blank/White Slides:</strong> {blankSlides.map(index => extractSlideNumber(images[index - 1]?.original || 'Unknown')).join(", ")}
                        </div>
                      )}
                      
                      {submissions.length === 0 ? (
                        <div className="text-center py-4">
                          <p className="text-muted">No submissions found for this workshop.</p>
                        </div>
                      ) : (
                        <Row>
                          {submissions.map((submission, index) => {
                            const slideNumber = submission.slide ? extractSlideNumber(submission.slide) : 'No slide';
                            const hasSlide = submission.slide !== null;
                            const hasAudio = submission.audio !== null;
                            const isCurrentAudio = currentAudio === submission.audio;
                            
                            return (
                              <Col key={index} xs={12} sm={6} md={4} lg={3} className="mb-4">
                                <Card className="h-100">
                                  <Card.Body className="d-flex flex-column">
                                    {hasSlide ? (
                                      <div 
                                        className="mb-3"
                                        style={{ 
                                          cursor: 'pointer',
                                          width: '100%'
                                        }}
                                        onClick={() => {
                                          const slideIndex = images.findIndex(img => img.original.includes(submission.student_id));
                                          if (slideIndex !== -1) {
                                            handleSlideClick(slideIndex);
                                          }
                                        }}
                                      >
                                        <img 
                                          src={`${submission.slide}${submission.slide.includes('?') ? '&' : '?'}t=${cacheBuster}`}
                                          alt={`Slide ${slideNumber}`}
                                          style={{ 
                                            width: '100%',
                                            height: '150px',
                                            objectFit: 'contain',
                                            backgroundColor: '#f8f9fa',
                                            borderRadius: '4px',
                                            border: blankSlides.includes(index + 1) ? '2px solid #ffc107' : 'none'
                                          }}
                                        />
                                        
                                        {/* Label below the image */}
                                        <div 
                                          className="d-flex justify-content-center align-items-center mt-2"
                                          style={{
                                            padding: '4px 8px'
                                          }}
                                        >
                                          <small className="text-dark fw-bold mb-0">
                                            {slideNumber}
                                            {blankSlides.includes(index + 1) && (
                                              <Badge bg="warning" text="dark" className="ms-1" style={{ fontSize: '0.6em' }}>
                                                Blank
                                              </Badge>
                                            )}
                                          </small>
                                        </div>
                                      </div>
                                    ) : (
                                      <div className="mb-3 text-center py-4">
                                        <small className="text-muted">No slide submitted</small>
                                      </div>
                                    )}
                                    
                                    {hasAudio && (
                                      <div className="mt-auto">
                                        <div className="d-flex justify-content-center align-items-center gap-2">
                                          <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            onClick={() => {
                                              if (isCurrentAudio && isAudioPlaying) {
                                                stopAudio();
                                              } else {
                                                playAudio(submission.audio);
                                              }
                                            }}
                                          >
                                            {isCurrentAudio && isAudioPlaying ? (
                                              <>
                                                <FaStop className="me-1" />
                                                Stop
                                              </>
                                            ) : (
                                              <>
                                                <FaPlay className="me-1" />
                                                Play
                                              </>
                                            )}
                                          </Button>
                                          {audioDurations[submission.audio] && (
                                            <small className="text-muted">
                                              {formatDuration(audioDurations[submission.audio])}
                                            </small>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                  </Card.Body>
                                </Card>
                              </Col>
                            );
                          })}
                        </Row>
                      )}
                    </>
                  )}
                </Card.Body>
              </Card>
            </div>
          ))}
      </div>
    </>
  );
};

export { Slideshow };
